import { useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import { useLocation, useNavigate } from 'react-router-dom'
import { has } from 'lodash'

import { Page } from 'common/interfaces'
import { useSessionDispatch, useSessionState } from 'context/Session'
import { getCompanySlug } from 'utils/flow'
import { useLocalStorage } from 'utils/hook'
import { adjustersRoute, firestoreClaimsPathName } from 'views/AdjustersHub/constants'
import { useAdjusterState } from 'views/AdjustersHub/context/adjusterContext'
import { initialFirebaseConfig } from 'views/AdjustersHub/firebase'
import { AirtableClaimResponse, HubSettings, SelectedClaim } from 'views/AdjustersHub/interfces'
import {
  useAdjusterFlow,
  useAdjustersHubSettings,
  useAirtableClaims,
} from 'views/AdjustersHub/utils/api'

import { StepperGroup } from '../ClaimView/components/Stepper/interface'

export function useCurrentAdjusterPage(): Page {
  const { data: flow } = useAdjusterFlow()
  const { currentPage } = useSessionState()
  const [{ flowKey }] = useLocalStorage<SelectedClaim>(
    'selectedClaim',
    {} as unknown as SelectedClaim
  )

  if (flow === undefined) {
    throw new Error('useCurrentAdjusterPage needs fetched flow to get current page')
  }

  const page = flow[flowKey].pages.find(({ id }) => {
    if (currentPage) {
      return id === currentPage
    }

    return flow[flowKey].start_page.id === id
  })

  if (page === undefined) {
    throw new Error('useCurrentAdjusterPage has no found current page via id')
  }

  return page
}

export function useCurrentClaimId(): string {
  const { pathname } = useLocation()
  return pathname.split('/')[2]
}

export function useFilteredClaims(): AirtableClaimResponse[] {
  const companySlug = getCompanySlug()

  const [airtableClaims] = useLocalStorage<AirtableClaimResponse[]>(
    `${companySlug}/airtableClaims`,
    []
  )

  const { isSuccess } = useAirtableClaims()

  const { data: hubSetData } = useAdjustersHubSettings()

  const { claimsSearch } = useAdjusterState()

  const filteredClaims = useMemo(() => {
    if (airtableClaims.length === 0 || !has(hubSetData, 'dictionary')) return []

    return airtableClaims.filter((el) =>
      el.fields[hubSetData?.dictionary.Claim as string]
        ?.toLowerCase()
        .includes(claimsSearch.toLowerCase())
    )
  }, [isSuccess, claimsSearch, airtableClaims, hubSetData])

  return filteredClaims
}

export function useSetHubSettingsToLS(): (data: HubSettings) => void {
  const companySlug = getCompanySlug()

  const [, setLS] = useLocalStorage(`${companySlug}/hubSettings`, {})
  const [, setLSFS] = useLocalStorage('firebaseSettings', {})

  return (data: HubSettings) => {
    setLS(data)

    if (data.firebase) {
      const { collection, credentials } = data.firebase

      setLSFS({
        collection: collection ?? firestoreClaimsPathName,
        firebaseConfig: credentials,
      })

      return
    }

    setLSFS({
      collection: firestoreClaimsPathName,
      firebaseConfig: initialFirebaseConfig,
    })
  }
}

export function useGoBackFromSubmittedClaim(): () => void {
  const navigate = useNavigate()
  const [, , clearSelectedClaim] = useLocalStorage<SelectedClaim>(
    'selectedClaim',
    {} as unknown as SelectedClaim
  )

  return () => {
    navigate(adjustersRoute)
    clearSelectedClaim()

    toast.success('This claim already submitted', {
      duration: 5000,
    })
  }
}

export function useSetIsDisabledAdjusterSubmit(stepsState?: StepperGroup): void {
  const [dispatchSession] = useSessionDispatch()

  useEffect(() => {
    if (stepsState) {
      const allCompleted = stepsState.every((step) => step.isComplete)
      dispatchSession({ type: 'setIsSubmitDisabled', isSubmitDisabled: !allCompleted })
    }
  }, [stepsState])
}
